import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

import Api from '@services/api';
import ClearButton from './clear-button';
import Loader from './loader';
import Results from './results';
import { TextInput } from 'nhsuk-react-components';
import styles from './hcpc.module.scss';
import { validateHcpcRecord } from './hcpc.utils';

const GMC_HCPC_ERROR = 'You cannot enter an HCPC PIN when a GMC PIN has been entered.';
const SELECT_RECORD_ERROR = 'Select a valid HCPC record from the list';

const Hcpc = forwardRef((props, ref) => {
    const inputRef = useRef(null);
    const debounceRef = useRef(null);
    const latestSearchRef = useRef(0);

    const [data, setData] = useState([]);
    const [dataLoading, setDataLoading] = useState(false);
    const [error, setError] = useState(undefined);
    const [focused, setFocused] = useState(-1);
    const [isReadOnly, setIsReadOnly] = useState(false);
    const [isValid, setIsValid] = useState(false);
    const [resultsVisible, setResultsVisible] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [acceptedRecord, setAcceptedRecord] = useState(null);
    const [validationReason, setValidationReason] = useState(null);
    // True while re-checking a previously-entered value against the API and
    // current person details on mount (e.g. reopening a closed section).
    const [initialRevalidating, setInitialRevalidating] = useState(false);

    // Keep a stable ref to the latest "outside click" handler so we can
    // register the window listener once, instead of re-binding a stale closure.
    const handleWindowClickedRef = useRef();
    handleWindowClickedRef.current = e => {
        if (e.target.type !== 'text') {
            setResultsVisible(false);
        }
    };

    useEffect(() => {
        const listener = e => handleWindowClickedRef.current(e);
        window.addEventListener('click', listener);

        return () => {
            window.removeEventListener('click', listener);
        };
    }, []);

    useEffect(() => {
        setIsReadOnly(props.question.isReadOnly);
    }, [props.question.isReadOnly]);

    useEffect(() => {
        return () => {
            // Cleanup pending searches when the component unmounts.
            clearTimeout(debounceRef.current);
            latestSearchRef.current += 1;
        };
    }, []);

    // No dependency array: `validate` (and everything it closes over - props,
    // isReadOnly, acceptedRecord, etc.) is redefined every render, so the
    // factory must re-run every render too. Otherwise a section-level
    // validator calling ref.current.validate() could invoke a stale closure
    // with outdated props (e.g. an old props.gmcValue or isReadOnly).
    useImperativeHandle(ref, () => ({
        validate,
        isValid,
        acceptedRecord,
        validationReason
    }));

    // HCPC cannot be entered when a GMC value already exists.
    const hasGmcValue = () => !!String(props.gmcValue ?? '').trim();

    // Normalise HCPC values for duplicate-safe / equality-safe comparison.
    const normaliseHcpcValue = value =>
        String(value ?? '')
            .trim()
            .replace(/\s+/g, ' ')
            .toUpperCase();

    // Single place that writes HCPC metadata back onto the question object,
    // so it survives parent re-renders and is visible synchronously (not
    // gated behind React's render cycle) to any external section validator.
    // NOTE: acceptedRecord/validationReason are custom fields bolted onto the
    // question object - they are NOT guaranteed to survive if the parent
    // ever reconstructs/replaces the question object (e.g. on section
    // close/reopen from stored form data). Do not rely on them persisting
    // across a remount - see the mount-time revalidation effect below.
    const persistQuestionMeta = ({
        acceptedRecord: rec = null,
        validationReason: reason = null
    }) => {
        props.question.acceptedRecord = rec;
        props.question.validationReason = reason;
    };

    // Persist validation state locally and on the question object.
    const setValidationError = (nextError, nextIsValid) => {
        setError(nextError);
        setIsValid(nextIsValid);

        props.question.error = nextError;
        props.question.isValid = nextIsValid;
    };

    // Single source of truth for "did this record satisfy validateHcpcRecord".
    const applyRecordValidation = record => {
        const result = validateHcpcRecord(record, props.enteredDetails || {}, {
            skipHcpcPersonValidation: props.skipHcpcPersonValidation
        });

        if (result.accepted) {
            setAcceptedRecord(record);
            setValidationReason(null);
            setValidationError(undefined, true);
            persistQuestionMeta({ acceptedRecord: record, validationReason: null });
        } else {
            setAcceptedRecord(null);
            setValidationReason(result.reason);
            setValidationError(result.reason, false);
            persistQuestionMeta({ acceptedRecord: null, validationReason: result.reason });
        }

        return result;
    };

    // On mount (e.g. reopening a previously-closed record), never trust any
    // persisted isValid/error/acceptedRecord flag at face value - those may
    // be stale, or may not have survived if the parent rebuilt the question
    // object. If there's a value in the field, re-fetch the matching HCPC
    // record from the API right now and run full validation against the
    // *current* person details/GMC value.
    useEffect(() => {
        let cancelled = false;

        const revalidateFromCurrentValue = async () => {
            const value = String(props.question.value ?? '').trim();

            if (!value) {
                setValidationError(undefined, true);
                return;
            }

            if (hasGmcValue()) {
                setAcceptedRecord(null);
                setValidationReason(GMC_HCPC_ERROR);
                setValidationError(GMC_HCPC_ERROR, false);
                persistQuestionMeta({ acceptedRecord: null, validationReason: GMC_HCPC_ERROR });
                return;
            }

            // Default to invalid while we re-check, so a section validator
            // calling validate() during this window fails safe rather than
            // trusting a stale flag.
            setAcceptedRecord(null);
            setInitialRevalidating(true);
            setValidationError(undefined, false);

            try {
                const api = new Api();
                const response = await api.getHCPCRefData(value);

                if (cancelled) return;

                const match = (response.data || []).find(
                    record =>
                        normaliseHcpcValue(record.Registration_Name) === normaliseHcpcValue(value)
                );

                if (!match) {
                    setValidationReason(SELECT_RECORD_ERROR);
                    setValidationError(SELECT_RECORD_ERROR, false);
                    persistQuestionMeta({
                        acceptedRecord: null,
                        validationReason: SELECT_RECORD_ERROR
                    });
                    return;
                }

                setSelectedRecord(match);
                applyRecordValidation(match);
            } catch {
                if (cancelled) return;

                // Network/API failure - don't silently mark it valid.
                const message = 'Unable to verify HCPC record, please try again';
                setValidationReason(message);
                setValidationError(message, false);
                persistQuestionMeta({ acceptedRecord: null, validationReason: message });
            } finally {
                if (!cancelled) {
                    setInitialRevalidating(false);
                }
            }
        };

        revalidateFromCurrentValue();

        return () => {
            cancelled = true;
        };
        // Deliberately mount-only: this specifically covers "value already
        // present when this component (re)appears". Live edits while mounted
        // are handled by handleOnInputChange / handleUpdateResults / the
        // enteredDetails-change effect further down.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Validate HCPC input, selected record state, and GMC conflict.
    // Called externally (e.g. by a section-level validator looping over all
    // questions) as well as internally. Must always reflect the *current*
    // props/state at call time - do not memoize this away.
    const validate = (requireAccepted = true, valueOverride) => {
        const value = String(valueOverride ?? props.question.value ?? '').trim();
        const hasValue = value.length > 0;

        if (initialRevalidating) {
            // Still confirming a pre-existing value against the API - fail
            // safe rather than reporting a premature result either way.
            setValidationError(SELECT_RECORD_ERROR, false);
            return false;
        }

        if (!hasValue) {
            setValidationError(undefined, true);
            return true;
        }

        if (hasGmcValue()) {
            setValidationError(GMC_HCPC_ERROR, false);
            return false;
        }

        const restoredAcceptedRecord = acceptedRecord || props.question.acceptedRecord;
        const restoredValidationReason = validationReason || props.question.validationReason;

        if (requireAccepted && !restoredAcceptedRecord) {
            setValidationError(restoredValidationReason || SELECT_RECORD_ERROR, false);
            return false;
        }

        setValidationError(undefined, true);
        return true;
    };

    // Reset accepted HCPC state when the user changes the typed value.
    const handleOnInputChange = async e => {
        let { value } = e.target;

        value = value
            .replace(/[^a-zA-Z0-9 ]/g, '')
            .replace(/\s+/g, ' ')
            .trimStart()
            .toUpperCase();

        if (value === props.question.value) return;

        setSelectedRecord(null);
        setAcceptedRecord(null);

        const errorMessage = value.length >= 3 ? SELECT_RECORD_ERROR : null;

        setValidationReason(errorMessage);
        persistQuestionMeta({ acceptedRecord: null, validationReason: errorMessage });
        setValidationError(errorMessage, false);

        handleResultsVisibility(value.length);

        await props.onChange({
            questionId: props.question.id,
            value
        });
    };

    const handleOnInputFocus = e => {
        handleResultsVisibility(e.target.value.length);
    };

    // Show HCPC results only when input is active, long enough, and editable.
    const handleResultsVisibility = count => {
        const minCount = 3;
        const isInputActive = inputRef.current === document.activeElement;

        if (hasGmcValue()) {
            clearTimeout(debounceRef.current);
            setResultsVisible(false);
            setData([]);
            return;
        }

        if (count >= minCount && isInputActive && !isReadOnly) {
            setResultsVisible(true);
            clearTimeout(debounceRef.current);
            // Debounce HCPC API searches to avoid searching on every key press.
            debounceRef.current = setTimeout(() => {
                search();
            }, 400);
        } else {
            setResultsVisible(false);
        }
    };

    const updateInputValue = async (questionId, value) => {
        await props.onChange({ questionId, value });
    };

    // Build a stable key for identifying duplicate HCPC records.
    const getHcpcResultKey = record =>
        [record?.Registration_Name, record?.First_Name, record?.Last_Name, record?.Gender]
            .map(normaliseHcpcValue)
            .join('|');

    // Remove duplicate HCPC records before rendering results.
    const dedupeHcpcResults = records => {
        const seen = new Set();

        return records.filter(record => {
            const key = getHcpcResultKey(record);

            if (seen.has(key)) return false;

            seen.add(key);
            return true;
        });
    };

    const search = async () => {
        const searchValue = inputRef.current?.value ?? '';

        if (searchValue.length < 3) {
            setData([]);
            setDataLoading(false);
            setResultsVisible(false);
            return;
        }

        const searchId = latestSearchRef.current + 1;
        latestSearchRef.current = searchId;

        setData([]);
        setDataLoading(true);

        try {
            const api = new Api();
            const response = await api.getHCPCRefData(searchValue);

            if (searchId !== latestSearchRef.current) return;

            if (response.data) {
                setData(dedupeHcpcResults(response.data));
                setFocused(-1);
            }
        } catch {
            if (searchId === latestSearchRef.current) {
                setFocused(-1);
            }
        } finally {
            if (searchId === latestSearchRef.current) {
                setDataLoading(false);
            }
        }
    };

    const handleUpdateResults = async selectedIndex => {
        const index = typeof selectedIndex === 'number' ? selectedIndex : focused;
        const record = data[index];

        if (!record) {
            setAcceptedRecord(null);
            setValidationReason(SELECT_RECORD_ERROR);
            setValidationError(SELECT_RECORD_ERROR, false);
            persistQuestionMeta({ acceptedRecord: null, validationReason: SELECT_RECORD_ERROR });
            return;
        }

        if (hasGmcValue()) {
            setAcceptedRecord(null);
            setValidationReason(GMC_HCPC_ERROR);
            persistQuestionMeta({ acceptedRecord: null, validationReason: GMC_HCPC_ERROR });
            setValidationError(GMC_HCPC_ERROR, false);
            return;
        }

        setSelectedRecord(record);
        await populateLinkedFields(record);
        setData([]);

        applyRecordValidation(record);
    };

    // Populate linked fields from the selected HCPC reference record.
    // Sequential awaits avoid a race between multiple concurrent onChange calls.
    const populateLinkedFields = async refData => {
        const linkedQuestions = props.question.linked_questions;

        for (const questionId in linkedQuestions) {
            await updateInputValue(linkedQuestions[questionId], refData?.[questionId]);
        }

        await updateInputValue(props.question.id, refData?.Registration_Name);

        if (props.handleDisable) {
            props.handleDisable({
                isReadOnly: !!refData?.Registration_Name,
                questionIds: [
                    props.question.id,
                    ...(linkedQuestions ? Object.values(linkedQuestions) : [])
                ]
            });
        }
    };

    // Clear HCPC input, results, validation state, and pending searches.
    const clearInputValue = e => {
        e.preventDefault();

        clearTimeout(debounceRef.current);
        latestSearchRef.current += 1;
        setData([]);
        setResultsVisible(false);
        setFocused(-1);
        setDataLoading(false);

        setSelectedRecord(null);
        setAcceptedRecord(null);
        setValidationReason(null);

        persistQuestionMeta({ acceptedRecord: null, validationReason: null });

        updateInputValue(props.question.id, '');

        if (props.handleDisable) {
            props.handleDisable({
                isReadOnly: false,
                questionIds: [
                    props.question.id,
                    ...(props.question.linked_questions
                        ? Object.values(props.question.linked_questions)
                        : [])
                ]
            });
        }
    };

    // Support keyboard navigation through HCPC search results.
    const handleOnKeyDown = e => {
        const numFocusableItems = data.length - 1;

        switch (e.key) {
            case 'ArrowDown':
                if (focused < numFocusableItems) {
                    const newFocus = focused + 1;
                    setFocused(newFocus);
                    handleResultsScroll(newFocus);
                }
                break;
            case 'ArrowUp':
                if (focused > 0) {
                    const newFocus = focused - 1;
                    setFocused(newFocus);
                    handleResultsScroll(newFocus);
                }
                break;
            case 'Escape':
                inputRef.current.blur();
                setResultsVisible(false);
                break;
            case 'Enter':
                e.preventDefault();
                handleUpdateResults();
                break;
            default:
                break;
        }
    };

    // Keep the focused dropdown result visible while navigating.
    const handleResultsScroll = focusedIndex => {
        const results = document.querySelector('#results');
        const item = document.querySelector('#results > li');

        if (results && item) {
            results.scrollTo({
                behavior: 'smooth',
                top: focusedIndex * item.getBoundingClientRect().height
            });
        }
    };

    // Revalidate the selected HCPC record when person details change
    // while this component remains mounted.
    useEffect(() => {
        if (!selectedRecord) return;

        applyRecordValidation(selectedRecord);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        props.enteredDetails?.firstName,
        props.enteredDetails?.surname,
        props.enteredDetails?.gender,
        props.enteredDetails?.profession,
        props.skipHcpcPersonValidation
    ]);

    // Revalidate HCPC when GMC changes to keep mutual exclusion in sync.
    useEffect(() => {
        const hasHcpcValue = !!String(props.question.value ?? '').trim();
        const hasQuestionError = !!props.question.error;

        if (hasHcpcValue || hasQuestionError) {
            validate(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.gmcValue]);

    const value = props.question.value ?? '';
    const hasHidden = props.question.hidden ?? true;

    if (hasHidden && props.question.hidden) return null;

    return (
        <div className={`nhsuk-u-clear ${styles.SearchWithResults}`}>
            <TextInput
                className={styles.SearchWithResultsInput}
                disabled={isReadOnly || initialRevalidating}
                error={error}
                hint={props.question.hint_text}
                id={props.id}
                inputRef={inputRef}
                label={props.question.name}
                onChange={handleOnInputChange}
                onFocus={handleOnInputFocus}
                onKeyDown={handleOnKeyDown}
                value={value}
                width="10"
                required={props.question.mandatory}
            />
            <Results
                data={data}
                focused={focused}
                handleClick={handleUpdateResults}
                setFocused={setFocused}
                visible={resultsVisible}
            />
            <ClearButton handleClick={clearInputValue} visible={isReadOnly} />
            <Loader loading={dataLoading || initialRevalidating} />
        </div>
    );
});

export default Hcpc;
